/**
 * Cash Documents Types
 *
 * TypeScript types for KP (Kasa Przyjęła) and KW (Kasa Wydała) documents.
 * Used for tracking cash register transactions in Polish accounting.
 */

/**
 * Cash document types (KP/KW)
 * Note: Values are lowercase to match backend camelCase JSON serialization
 */
export enum CashDocumentType {
  KP = 'kp', // Kasa Przyjęła (Cash Received) - lowercase for API
  KW = 'kw', // Kasa Wydała (Cash Disbursed) - lowercase for API
}

/**
 * Cash document type labels (Polish)
 */
export const CashDocumentTypeLabels: Record<CashDocumentType, string> = {
  [CashDocumentType.KP]: 'Kasa Przyjęła',
  [CashDocumentType.KW]: 'Kasa Wydała',
};

/**
 * Main DTO for cash document
 */
export interface CashDocumentDto {
  id: number;
  type: CashDocumentType;
  typeLabel?: string;
  documentNumber: string;
  amount: number;
  currency: string;
  documentDate: string; // ISO date format (YYYY-MM-DD)
  personName: string;
  personAddress?: string;
  purpose?: string;
  description?: string;
  note?: string;
  pdfFilePath?: string;
  reservationId?: number;
  invoiceId?: number;
  invoicePaymentId?: number;
  issuedBy?: string;
  companyId?: number | null;
  createdAt: string; // ISO datetime
  updatedAt?: string; // ISO datetime
}

/**
 * Request DTO for creating cash document
 */
export interface CreateCashDocumentRequest {
  type: CashDocumentType;
  documentNumber?: string; // Auto-generated if not provided
  amount: number;
  currency?: string; // Default: PLN
  documentDate?: string; // Default: today
  personName: string;
  purpose?: string;
  note?: string;
  reservationId?: number;
  invoiceId?: number;
  issuedBy?: string;
  companyId?: number | null; // Required for manual create (no reservationId/invoiceId)
}

/**
 * Request DTO for updating cash document
 * Only editable fields included
 */
export interface UpdateCashDocumentRequest {
  id: number;
  amount?: number;
  documentDate?: string;
  personName?: string;
  purpose?: string;
  note?: string;
  issuedBy?: string;
}

/**
 * Response DTO for cash balance query
 */
export interface CashBalanceResponse {
  balance: number;
  totalKP: number;
  totalKW: number;
  kpCount: number;
  kwCount: number;
  from: string; // ISO date
  to: string; // ISO date
  currency: string;
}

/**
 * Helper: Get type label in Polish
 */
export const getTypeLabel = (type: CashDocumentType): string => {
  return CashDocumentTypeLabels[type];
};

/**
 * Helper: Format amount with currency
 */
export const formatAmount = (amount: number, currency: string = 'PLN'): string => {
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

/**
 * Helper: Check if document is KP (received)
 */
export const isReceived = (doc: CashDocumentDto): boolean => {
  return doc.type === CashDocumentType.KP;
};

/**
 * Helper: Check if document is KW (disbursed)
 */
export const isDisbursed = (doc: CashDocumentDto): boolean => {
  return doc.type === CashDocumentType.KW;
};

/**
 * Helper: Get type color for MUI Chip
 */
export const getTypeColor = (type: CashDocumentType): 'success' | 'error' => {
  return type === CashDocumentType.KP ? 'success' : 'error';
};

/**
 * Helper: Get type icon
 */
export const getTypeIcon = (type: CashDocumentType): string => {
  return type === CashDocumentType.KP ? '🟢' : '🔴';
};

/**
 * Helper: Calculate balance from documents
 */
export const calculateBalance = (documents: CashDocumentDto[]): number => {
  const received = documents
    .filter((d) => d.type === CashDocumentType.KP)
    .reduce((sum, d) => sum + d.amount, 0);

  const disbursed = documents
    .filter((d) => d.type === CashDocumentType.KW)
    .reduce((sum, d) => sum + d.amount, 0);

  return received - disbursed;
};

/**
 * Helper: Get balance color
 */
export const getBalanceColor = (balance: number): 'success' | 'error' | 'default' => {
  if (balance > 0) return 'success';
  if (balance < 0) return 'error';
  return 'default';
};

/**
 * Helper: Get balance label
 */
export const getBalanceLabel = (balance: number): string => {
  if (balance > 0) return 'Nadwyżka gotówki';
  if (balance < 0) return 'Niedobór gotówki';
  return 'Kasa zbilansowana';
};
